import { useTranslation } from 'react-i18next';
import { FreelancerSearchResult } from '../../types/search';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import RatingStars from '../common/RatingStars';
import { twMerge } from 'tailwind-merge';
import ProfileAvatar from '../common/ProfileAvatar';
import AlliancesHeadshots from '../alliances/AlliancesHeadshots';

interface FreelancerProfileModalContentProps {
  freelancer: FreelancerSearchResult;
  className?: string;
}

const FreelancerProfileModalContent = ({
  freelancer,
  className,
}: FreelancerProfileModalContentProps) => {
  const { t } = useTranslation(['freelancers']);

  return (
    <div className={twMerge(`flex flex-col gap-4 md:flex-row md:justify-between`, className)}>
      <div className="w-24 h-24 rounded-full bg-gray-200 flex-shrink-0 bg-[#EAF1F7] hidden items-center justify-center text-secondary font-bold text-2xl md:flex">
        <ProfileAvatar
          profile_id={freelancer.id}
          first_name={freelancer.first_name}
          avatar_url={freelancer.avatar_url}
          className="w-full h-full"
        />
      </div>
      <h2 className="text-2xl font-bold text-secondary md:pt-6">
        {freelancer.first_name} {freelancer.last_name}
      </h2>
      <div className="flex flex-col gap-2">
        <div className="text-sm text-secondary">
          {t('offeredServices', { ns: 'freelancers', defaultValue: 'Services offerts' })}
        </div>
        <div className="font-bold flex flex-wrap">{getFreelancerSubtitle(freelancer, t)}</div>
      </div>

      {/* Distance (Desktop) */}
      <div className="flex flex-col gap-2 hidden md:flex">
        <span className="text-sm text-secondary">
          {t('distance', { ns: 'freelancers', defaultValue: 'Distance' })}
        </span>
        <div className="font-bold text-sm text-secondary">
          {freelancer.distance !== undefined && freelancer.distance !== null
            ? `${Math.round(freelancer.distance)} km`
            : '-'}
        </div>
      </div>
      {/* Fin de la section déplacée */}

      <div className="flex items-center justify-between md:flex-row-reverse md:items-start md:gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-sm text-secondary">
            {t('rating', { ns: 'freelancers', defaultValue: 'Évaluation' })}
          </span>
          <span className="align-middle">
            <RatingStars rating={freelancer.overall_rating} />
          </span>
        </div>

        {/* Distance (Mobile) - Reste à sa position originale */}
        <div className="flex flex-col gap-2 flex md:hidden">
          <span className="text-sm text-secondary">
            {t('distance', { ns: 'freelancers', defaultValue: 'Distance' })}
          </span>
          <div className="font-bold text-sm text-secondary">
            {freelancer.distance !== undefined && freelancer.distance !== null
              ? `${Math.round(freelancer.distance)} km`
              : '-'}
          </div>
        </div>
      </div>

      {freelancer.recent_alliances && freelancer.recent_alliances.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-secondary">
            {t('alliances', { ns: 'freelancers', defaultValue: 'Alliances' })}
          </span>
          <AlliancesHeadshots
            alliances={freelancer.recent_alliances}
            allianceCount={freelancer.alliance_count}
            maxDisplayed={10}
            maxCount={10}
            avatarSize="w-6 h-6"
          />
        </div>
      )}
    </div>
  );
};

export default FreelancerProfileModalContent;
